<template>
  <div class="step-form-builder">
    <div class="row mb-3">
      <div class="col-12">
        <h4>Definir campos a completar</h4>
        <p class="text-primary mb-0">
          Posiciona sobre el documento los campos que el responsable del paso
          de <b>completitud de datos</b> deberá llenar.
        </p>
      </div>
    </div>

    <div class="row">
      <aside class="col-3 step-form-builder__sidebar">
        <label class="step-form-builder__label">Tipo de campo a crear</label>
        <div class="step-form-builder__palette">
          <b-button
            v-for="t in fieldTypeList"
            :key="t.type"
            size="sm"
            :variant="t.type === activeFieldType ? 'primary' : 'outline-primary'"
            class="step-form-builder__palette-btn"
            @click="toggleType(t.type)"
          >
            {{ t.label }}
          </b-button>
        </div>
        <p class="step-form-builder__hint">
          <template v-if="activeFieldType">
            Haz click sobre el PDF para crear un campo
            <strong>{{ activeLabel }}</strong>. Se agrega de a uno.
          </template>
          <template v-else>
            Selecciona un tipo para agregar un campo.
          </template>
        </p>

        <div class="step-form-builder__actions">
          <b-button
            size="sm"
            variant="outline-danger"
            :disabled="!fields.length"
            @click="clearFields"
          >
            Vaciar campos
          </b-button>
          <span class="step-form-builder__count">{{ fields.length }} campo(s)</span>
        </div>

        <label class="step-form-builder__label">Campos agregados</label>
        <FieldListPanel
          class="step-form-builder__list"
          :fields="fields"
          :selected-id="selectedId"
          @select="selectedId = $event"
          @delete="deleteField"
          @update="updateField"
        />
      </aside>

      <main class="col-9 step-form-builder__main">
        <PdfFieldEditor
          v-if="hasMainFile"
          v-model="fields"
          :path="step.mainFile.path"
          :active-field-type="activeFieldType"
          :allowed-field-types="allowedFieldTypes"
          :selected-id.sync="selectedId"
          :name="step.mainFile.name"
          @field-created="onFieldCreated"
          @loaded="$emit('loaded')"
        />
        <div v-else class="step-form-builder__empty">
          No hay documento principal cargado para definir campos.
        </div>
      </main>
    </div>
  </div>
</template>

<script>

import WizardStep from '@/febos/chile/dnt/mixins/WizardStep';
import PdfFieldEditor from '@/febos/global/pdf/formBuilder/components/PdfFieldEditor';
import FieldListPanel from '@/febos/global/pdf/formBuilder/components/FieldListPanel';
import {
  DATA_FIELD_TYPES,
  defaultLabelForType,
} from '@/febos/global/pdf/formBuilder/model/fieldModel';

/**
 * StepFormBuilder — paso del wizard de nuevos flujos que monta el constructor
 * de campos (PdfFieldEditor) cuando el flujo incluye un paso de tipo
 * "Completitud de datos" (TIPO_PROCESO = 9).
 *
 * Los campos se definen a nivel de flujo (un único PDF principal) y se guardan
 * en `step.completionFields`. El mapper los serializa a la columna
 * `configuration` del paso como JSON `{ campos: [...] }`.
 */
export default {
  name: 'StepFormBuilder',
  mixins: [WizardStep],
  components: {
    PdfFieldEditor,
    FieldListPanel,
  },
  data() {
    return {
      allowedFieldTypes: [...DATA_FIELD_TYPES],
      activeFieldType: null,
      selectedId: null,
      step: {
        completionFields: [],
        mainFile: {},
        ...this.draft,
      },
    };
  },
  computed: {
    fields: {
      get() {
        return this.step.completionFields || [];
      },
      set(value) {
        this.step.completionFields = value;
      },
    },
    hasMainFile() {
      return !!(this.step.mainFile && this.step.mainFile.path);
    },
    fieldTypeList() {
      return this.allowedFieldTypes.map((type) => ({
        type,
        label: defaultLabelForType(type),
      }));
    },
    activeLabel() {
      return this.activeFieldType ? defaultLabelForType(this.activeFieldType) : '';
    },
  },
  methods: {
    toggleType(type) {
      this.activeFieldType = this.activeFieldType === type ? null : type;
    },
    clearFields() {
      this.fields = [];
      this.selectedId = null;
    },
    deleteField(id) {
      this.fields = this.fields.filter((f) => f.id !== id);
      if (this.selectedId === id) {
        this.selectedId = null;
      }
    },
    updateField(updated) {
      this.fields = this.fields.map((f) => (f.id === updated.id ? updated : f));
    },
    onFieldCreated() {
      // Modo "sello único": tras agregar, se deselecciona el tipo para evitar
      // que un segundo click cree un campo duplicado.
      this.activeFieldType = null;
    },
    getStepData() {
      return {
        ...this.step,
      };
    },
    async isValid() {
      if (!this.fields.length) {
        this.$bvToast.toast(
          'Debes agregar al menos un campo para completar',
          {
            title: 'Error',
            variant: 'danger',
            pauseOnHover: true,
            solid: true,
            autoHideDelay: 2000,
          },
        );
        return false;
      }
      return true;
    },
  },
};

</script>

<style lang="scss" scoped>

.step-form-builder__sidebar {
  display: flex;
  flex-direction: column;
}

.step-form-builder__label {
  font-size: 12px;
  font-weight: 600;
  color: #6b6b85;
  margin: 10px 0 4px;
}

.step-form-builder__palette {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.step-form-builder__palette-btn {
  flex: 0 0 auto;
}

.step-form-builder__hint {
  font-size: 11px;
  color: #8a8aa0;
  margin: 10px 0;
}

.step-form-builder__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 8px 0;
}

.step-form-builder__count {
  font-size: 12px;
  color: #6b6b85;
}

.step-form-builder__list {
  margin-bottom: 4px;
}

.step-form-builder__main {
  display: flex;
  min-height: 480px;
}

.step-form-builder__empty {
  margin: auto;
  color: #8a8aa0;
}

</style>
